<template>
  <base-datepicker ref="picker" />
</template>

<script>
import BaseDatepicker from 'vue3-datepicker';

/**
 * `vue3-datepicker`, with a second click on the box closing the calendar again.
 *
 * <b>The library has no toggle at all.</b> It binds three handlers to the input:
 *
 * <pre>
 *   onFocus → renderView(initialView)   opens
 *   onClick → renderView(initialView)   opens AGAIN — the same view, every time
 *   onBlur  → renderView()              closes
 * </pre>
 *
 * So a second click on an open picker re-opens it, and the only way to dismiss one is to click
 * somewhere else on the page. That is a guess the user has to make, and on a touch screen «somewhere
 * else» is often another control they did not mean to press.
 *
 * <b>Why the listeners are attached in `mounted` and not in the template.</b> The library sets
 * `inheritAttrs: false` and forwards attributes to its inner `<input>`, so a listener written on the
 * component tag lands on the input itself — the same element the library's own handler is on, where
 * ordering would depend on `stopImmediatePropagation` and on who registered first. On the root
 * element in the CAPTURE phase there is no such race: we see the event before anything inside does.
 *
 * Nothing is wrapped in an extra element either. These pickers sit in flex rows carrying widths and
 * `form-control`; an added div would become the flex item instead of the picker and move things.
 *
 * <b>Every attribute and `v-model` still works</b>: this component declares no props, so the single
 * component at the root of its template receives all of them by ordinary fallthrough.
 *
 * <b>Use this instead of importing `vue3-datepicker` directly</b> — the import name stays `Datepicker`
 * at every call site, so templates and registrations are unchanged.
 */
export default {
  name: 'ToggleDatepicker',
  components: { BaseDatepicker },
  data() {
    return {
      /* mousedown decides; click has to be swallowed separately, or the library reopens what we just
         closed a few milliseconds later. */
      swallowClick: false
    };
  },
  mounted() {
    const el = this.$refs.picker && this.$refs.picker.$el;
    if (!el) {
      return;
    }
    el.addEventListener('mousedown', this.onMouseDown, true);
    el.addEventListener('click', this.onClick, true);
  },
  beforeUnmount() {
    const el = this.$refs.picker && this.$refs.picker.$el;
    if (!el) {
      return;
    }
    el.removeEventListener('mousedown', this.onMouseDown, true);
    el.removeEventListener('click', this.onClick, true);
  },
  methods: {
    onMouseDown(event) {
      // Only the box. A click on a day, a month or an arrow inside the open calendar must reach it.
      if (!event.target || event.target.tagName !== 'INPUT') {
        return;
      }
      const picker = this.$refs.picker;
      if (!picker || picker.viewShown === 'none') {
        return;   // closed: let it open exactly as before
      }
      picker.renderView('none');
      // Keep focus where it is. Without this the input is re-focused and onFocus opens it again.
      event.preventDefault();
      this.swallowClick = true;
    },

    onClick(event) {
      if (!this.swallowClick) {
        return;
      }
      this.swallowClick = false;
      event.stopPropagation();
    }
  }
};
</script>
